import { Query, QueryClient } from "@tanstack/react-query";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import {
    AccrualReviewApproveResponse,
    AccrualReviewUndoResponse,
    BulkApproveAccrualReviewResponse,
} from "@common/schemas/domains/accrualReview/accrual-review.schema";
import {
    ACCRUAL_REVIEW_QUERY_KEY,
    TRANSACTIONS_QUERY_KEY,
} from "@components/transactions/useTransactionsQuery";
import { TRANSACTIONS_STATISTICS_QUERY_KEY } from "@components/transactions/useTransactionsStatisticsQuery";
import { TRANSACTION_DETAILS_QUERY_KEY } from "@components/transactions/TransactionDetails/useTransactionDetailsQuery";
import { BILL_ACCRUAL_MATCHES_QUERY_KEY } from "@api/bill.api";
import { INVOICE_ACCRUAL_MATCHES_QUERY_KEY } from "@api/invoice.api";
import { queryClient } from "../../../queryClient";

export const DOCUMENT_MATCH_INVOICE_QUERY_KEY = "document-match-invoice";
export const DOCUMENT_MATCH_BILL_QUERY_KEY = "document-match-bill";

interface TransactionsQueryData {
    data: TransactionListItem[];
    total?: number;
}

function updateAccrualReviewCache(
    fn: (data: TransactionListItem[]) => TransactionListItem[],
    predicate?: (query: Query) => boolean,
) {
    const entries = queryClient.getQueriesData<TransactionsQueryData>({
        queryKey: [ACCRUAL_REVIEW_QUERY_KEY],
        predicate,
    });

    for (const [key, prev] of entries) {
        if (!prev?.data) {
            continue;
        }
        const newData = fn(prev.data);
        const countDiff = newData.length - prev.data.length;
        queryClient.setQueryData(key, {
            ...prev,
            data: newData,
            total: prev.total != null ? prev.total + countDiff : undefined,
        });
    }
}

/** Invoice rows show the payment's schedule once matched, and drop it on undo. */
function invalidateInvoiceRows() {
    queryClient.invalidateQueries({ queryKey: ["invoices"] });
}

function invalidateAccrualReviewQueries() {
    queryClient.invalidateQueries({
        queryKey: [ACCRUAL_REVIEW_QUERY_KEY],
        refetchType: "none",
    });
}

/**
 * Refresh the transactions-statistics cache so any UI bound to a count
 * (`transactionCount` in the paginated-transactions context, the
 * "Confirmed N of M" sub-header in the accrual review task body, etc.)
 * updates immediately after an accrual approve/undo. We don't touch
 * `ACCRUAL_REVIEW_QUERY_KEY` here — the list cache has just been
 * surgically updated above and refetching would drop just-confirmed rows
 * from `isNotAccrualReviewed: true` views.
 */
function refreshTransactionsStatistics() {
    queryClient.invalidateQueries({
        queryKey: [TRANSACTIONS_STATISTICS_QUERY_KEY],
    });
}

/**
 * Invalidate every cache that can be affected by an accrual match
 * approve/undo — shared by the AccrualMatchDrawer (approve/save) and
 * the AccrualMatchedTransactionsSection (per-row detach).
 *
 * Pass `transactionId` to narrow the transaction-details invalidation
 * to a single transaction; omit to invalidate all.
 *
 * Pass `skipAccrualReviewList: true` when the caller has already applied
 * a surgical in-place update to the accrual review list cache (see
 * `applyApproveToAccrualReviewCache`). This avoids a refetch that would
 * drop the just-approved transaction from views filtered by
 * `isNotAccrualReviewed: true` (e.g. the accrual review page, accrual
 * review tasks).
 */
export function invalidateAccrualMatchCaches(
    client: QueryClient,
    {
        transactionId,
        skipAccrualReviewList,
    }: { transactionId?: number; skipAccrualReviewList?: boolean } = {},
) {
    client.invalidateQueries({
        queryKey: [BILL_ACCRUAL_MATCHES_QUERY_KEY],
    });
    client.invalidateQueries({
        queryKey: [INVOICE_ACCRUAL_MATCHES_QUERY_KEY],
    });
    client.invalidateQueries({ queryKey: ["bills"] });
    client.invalidateQueries({ queryKey: ["invoices"] });
    client.invalidateQueries({ queryKey: ["accrual-match-invoices"] });
    client.invalidateQueries({ queryKey: ["accrual-match-bills"] });
    client.invalidateQueries({ queryKey: ["accrual-match-transactions"] });
    client.invalidateQueries({
        queryKey: ["accrual-match-suggested-invoices"],
    });
    client.invalidateQueries({ queryKey: ["accrual-match-suggested-bills"] });
    client.invalidateQueries({
        queryKey: ["accrual-match-suggested-transactions"],
    });
    client.invalidateQueries({ queryKey: [DOCUMENT_MATCH_INVOICE_QUERY_KEY] });
    client.invalidateQueries({ queryKey: [DOCUMENT_MATCH_BILL_QUERY_KEY] });
    if (!skipAccrualReviewList) {
        client.invalidateQueries({ queryKey: [ACCRUAL_REVIEW_QUERY_KEY] });
    }
    // Refresh the regular transactions list + statistics so the
    // consolidated "Matched transfer" cell and accrual-aware totals see
    // the new accrualMatches without a manual refresh. The accrual review
    // list uses ACCRUAL_REVIEW_QUERY_KEY (gated above by
    // skipAccrualReviewList) so it is not double-invalidated here.
    client.invalidateQueries({ queryKey: [TRANSACTIONS_QUERY_KEY] });
    client.invalidateQueries({
        queryKey: [TRANSACTIONS_STATISTICS_QUERY_KEY],
    });
    client.invalidateQueries({
        queryKey:
            transactionId != null
                ? [TRANSACTION_DETAILS_QUERY_KEY, transactionId]
                : [TRANSACTION_DETAILS_QUERY_KEY],
    });
    // The Recognition schedules Match chip reads relatedLinks from these
    // lists. A match save does not write that cache, so the chip stays
    // on the previous matches until the list is invalidated. Lifecycle
    // previews are a different query and stay as they are.
    client.invalidateQueries({
        queryKey: ["schedule-templates"],
        predicate: (query) => {
            const segment = query.queryKey[2];
            return (
                segment === "workspace-applications" ||
                segment === "applications"
            );
        },
    });
}

export function applyApproveToAccrualReviewCache(
    transactionId: number,
    {
        updatedTransactions,
        createdSplits,
    }: Pick<
        AccrualReviewApproveResponse,
        "updatedTransactions" | "createdSplits"
    >,
) {
    const updatesById = new Map(updatedTransactions.map((t) => [t.id, t]));

    updateAccrualReviewCache((list) => {
        const result: TransactionListItem[] = [];

        for (const t of list) {
            const update = updatesById.get(t.id);
            if (update) {
                result.push({ ...t, ...update });
                if (t.id === transactionId && createdSplits.length > 0) {
                    for (const split of createdSplits) {
                        result.push({ ...t, ...split });
                    }
                }
            } else {
                result.push(t);
            }
        }

        return result;
    });
    invalidateAccrualReviewQueries();
    invalidateInvoiceRows();
    refreshTransactionsStatistics();
}

export function applyBulkApproveToAccrualReviewCache({
    updatedTransactions,
    createdSplits,
}: Pick<
    BulkApproveAccrualReviewResponse,
    "updatedTransactions" | "createdSplits"
>) {
    const updatesById = new Map(updatedTransactions.map((t) => [t.id, t]));
    const splitsByParent = new Map<number, typeof createdSplits>();
    for (const split of createdSplits) {
        const parentId = split.splitParentId;
        if (parentId == null) {
            continue;
        }
        const existing = splitsByParent.get(parentId) ?? [];
        existing.push(split);
        splitsByParent.set(parentId, existing);
    }

    updateAccrualReviewCache((list) => {
        const result: TransactionListItem[] = [];
        for (const t of list) {
            const update = updatesById.get(t.id);
            if (update) {
                result.push({ ...t, ...update });
                const splits = splitsByParent.get(t.id);
                if (splits) {
                    for (const split of splits) {
                        result.push({ ...t, ...split });
                    }
                }
            } else {
                result.push(t);
            }
        }
        return result;
    });

    invalidateAccrualReviewQueries();
    invalidateInvoiceRows();
    refreshTransactionsStatistics();
}

/** Filters sit third in the key built by `buildTransactionsQueryKey`. */
const isReviewedListQuery = ({ queryKey: [, , filters] }: Query): boolean =>
    typeof filters === "object" &&
    filters !== null &&
    "isAccrualReviewed" in filters &&
    filters.isAccrualReviewed === true;

/**
 * Drops just-reverted rows from cached Reviewed lists (`isAccrualReviewed:
 * true`). For read-only Reviewed tables, which can't re-confirm a row in
 * place; editable tables keep it so the user can confirm it again. Re-marks
 * every accrual review list stale afterwards, because the cache writes here
 * would otherwise make them look fresh and skip their refetch on view.
 */
export function removeRevertedFromReviewedAccrualReviewCache({
    updatedTransactions,
}: Pick<AccrualReviewUndoResponse, "updatedTransactions">) {
    const revertedIds = new Set(
        updatedTransactions
            .filter((transaction) => !transaction.accrualAccountId)
            .map((transaction) => transaction.id),
    );
    updateAccrualReviewCache(
        (list) =>
            list.filter((transaction) => !revertedIds.has(transaction.id)),
        isReviewedListQuery,
    );
    invalidateAccrualReviewQueries();
}

export function applyUndoToAccrualReviewCache({
    updatedTransactions,
    deletedSplitIds,
}: Pick<AccrualReviewUndoResponse, "updatedTransactions" | "deletedSplitIds">) {
    const deletedSet = new Set(deletedSplitIds);
    const updatesById = new Map(updatedTransactions.map((t) => [t.id, t]));

    updateAccrualReviewCache((list) =>
        list
            .filter((t) => !deletedSet.has(t.id))
            .map((t) => {
                const update = updatesById.get(t.id);
                return update ? { ...t, ...update } : t;
            }),
    );
    invalidateAccrualReviewQueries();
    invalidateInvoiceRows();
    refreshTransactionsStatistics();
}
